import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios"
import { AxiosHeaders } from "axios"

import { api } from "@/api"
import { HTTP_STATUS_CODE, KEY_VALUES } from "@/constants"
import i18n from "@/i18n"
import { getBrowserQueryClient } from "@/providers"

import { StorageHelper, StringHelper } from "."
export const validateStatus = (status: number) => {
  return (
    status >= HTTP_STATUS_CODE.SUCCESS &&
    status < HTTP_STATUS_CODE.MULTIPLE_CHOICES
  )
}
export const handleError = (error: AxiosError) => {
  const data = error?.response?.data as ErrorResponseType
  const {
    UNAUTHORIZED,
    INTERNAL_SERVER_ERROR,
    BAD_REQUEST,
  } = HTTP_STATUS_CODE
  const {
    X_ACCESS_TOKEN_KEY,
    INVALID_ACCESS_TOKEN_KEY,
    AUTH_LOCAL_STORAGE_KEY,
  } = KEY_VALUES
  const handleUnauthorizedError = (error: AxiosError) => {
    const status = error?.response?.status

    // Verificar se é erro de primeiro login
    const isFirstLogin = data?.cause?.errors?.some(
      (err) => err.error === "FIRST_LOGIN"
    )

    if (status === UNAUTHORIZED && isFirstLogin) {
      return {
        statusCode: status,
        message:
          data?.cause?.errors?.[0]?.message ||
          i18n.t("auth.firstLoginDetected"),
        requestId: data?.requestId,
        isFirstLogin: true,
      }
    }

    // Apenas fazer logout se for 401 (token inválido), não em 403 (sem permissão)
    if (
      status === UNAUTHORIZED &&
      data?.message === INVALID_ACCESS_TOKEN_KEY
    ) {
      delete api?.defaults.headers[X_ACCESS_TOKEN_KEY]
      StorageHelper.removeItem(X_ACCESS_TOKEN_KEY)
      StorageHelper.removeItem(AUTH_LOCAL_STORAGE_KEY)

      // Limpar cache do QueryClient
      const queryClient = getBrowserQueryClient()
      if (queryClient) {
        queryClient.clear()
      }

      return {
        statusCode: status,
        message: i18n.t("apiErrors.invalidToken"),
        requestId: data?.requestId,
        isForbidden: true,
      }
    }
  }
  if (error?.response) {
    const status = error.response.status

    // Erros 5XX (Server Errors) - usar mensagem padrão
    if (status >= INTERNAL_SERVER_ERROR) {
      return {
        statusCode: status,
        message: i18n.t("apiErrors.serverUnavailable"),
        requestId: data?.requestId,
      }
    }

    // Erros 4XX (Client Errors) - verificar primeiro se é INVALID_ACCESS_TOKEN
    if (
      status >= BAD_REQUEST &&
      status < INTERNAL_SERVER_ERROR
    ) {
      // Verificar se é erro de token inválido (pode vir em qualquer 4XX)
      const unauthorizedError =
        handleUnauthorizedError(error)
      if (unauthorizedError) {
        return unauthorizedError
      }

      // Se não for token inválido, mostrar mensagem do backend
      const backendMessage = data?.cause?.errors
        ?.map((err) => err.message)
        .filter(Boolean)
        .join(", ")

      return {
        statusCode: data?.responseCode || status,
        message:
          backendMessage ||
          data?.message ||
          i18n.t("apiErrors.unknownError"),
        requestId: data?.requestId,
      }
    }

    // Outros status codes
    return {
      statusCode: status,
      message:
        data?.message || i18n.t("apiErrors.unknownError"),
      requestId: data?.requestId,
    }
  } else if (error?.request) {
    const isConnectionRefused =
      error.code === "ERR_NETWORK" ||
      error.code === "ERR_CONNECTION_REFUSED" ||
      error.message?.includes("Network Error")
    return {
      statusCode: BAD_REQUEST,
      message: isConnectionRefused
        ? i18n.t("apiErrors.serverUnavailable")
        : i18n.t("apiErrors.checkInternet"),
      requestId: data?.requestId,
      isConnectionError: isConnectionRefused,
    }
  } else {
    const isConnectionRefused =
      error.code === "ERR_NETWORK" ||
      error.code === "ERR_CONNECTION_REFUSED"
    return {
      statusCode: error?.code || "UNKNOWN",
      message: isConnectionRefused
        ? i18n.t("apiErrors.serverUnavailable")
        : i18n.t("apiErrors.unknownError"),
      requestId: data?.requestId,
      isConnectionError: isConnectionRefused,
    }
  }
}
const PUBLIC_ENDPOINTS = [
  "/oauth/login",
  "/oauth/forgot_password",
  "/oauth/forgot_password/confirm",
  "/oauth/first_login",
  "/sso/",
  "/domains/",
]

const isPublicEndpoint = (url?: string): boolean => {
  if (!url) return false
  return PUBLIC_ENDPOINTS.some((endpoint) =>
    url.includes(endpoint)
  )
}

export const interceptorRequest = async (
  request: InternalAxiosRequestConfig
) => {
  const ensureHeaders = () => {
    if (!request.headers)
      request.headers = new AxiosHeaders()
    return request.headers
  }

  const hasHeader = (key: string) => {
    const headers = ensureHeaders() as unknown as {
      has?: (k: string) => boolean
      get?: (k: string) => unknown
      [k: string]: unknown
    }
    if (typeof headers.has === "function")
      return headers.has(key)
    if (typeof headers.get === "function") {
      return headers.get(key) !== undefined
    }
    const lowerKey = key.toLowerCase()
    return Object.keys(headers).some(
      (k) => k.toLowerCase() === lowerKey
    )
  }

  const setHeader = (key: string, value: string) => {
    const headers = ensureHeaders() as unknown as {
      set?: (k: string, v: string) => void
      [k: string]: unknown
    }
    if (typeof headers.set === "function") {
      headers.set(key, value)
      return
    }
    headers[key] = value
  }

  const {
    X_ACCESS_TOKEN_KEY,
    AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID,
  } = KEY_VALUES
  const sessionToken = StorageHelper.getItem(
    X_ACCESS_TOKEN_KEY
  )

  // Abortar requisições que requerem autenticação se não há token
  if (!sessionToken && !isPublicEndpoint(request.url)) {
    const controller = new AbortController()
    controller.abort()
    request.signal = controller.signal
    return request
  }

  if (
    sessionToken &&
    !StringHelper.isNullOrEmpty(sessionToken)
  ) {
    setHeader(X_ACCESS_TOKEN_KEY, sessionToken)
  }

  const whitelabelUuid = StorageHelper.getItem(
    AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID
  )
  if (
    whitelabelUuid &&
    !StringHelper.isNullOrEmpty(whitelabelUuid)
  ) {
    if (!hasHeader("x-white-label-uuid")) {
      setHeader("x-white-label-uuid", whitelabelUuid)
    }
  }
  return request
}
export const interceptorResponse = async (
  response: AxiosResponse
) => {
  return response
}
